import React, { useMemo, useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import {
  TradingViewChartWidget,
} from "@crypto-trading/ui-shared/client";
import { TokenAnalysisSection } from "@/components/TokenAnalysisSection";
import { useUserExchanges } from "@/hooks/useExchanges";
import { useTokenAvailability, useTokenData } from "@/hooks/useMarketData";
import { ensureUsdtSymbol, extractBaseCurrency } from "@/utils/symbols";

interface TokenAnalysisDrawerDesktopProps {
  userId: string;
  symbol: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTrade?: (symbol: string) => void;
}

const SHEET_WIDTH = 500;

export const TokenAnalysisDrawerDesktop: React.FC<
  TokenAnalysisDrawerDesktopProps
> = ({ userId, symbol, open, onOpenChange, onTrade }) => {
  const normalizedSymbol = symbol ? ensureUsdtSymbol(symbol) : "";
  const baseCurrency = normalizedSymbol
    ? extractBaseCurrency(normalizedSymbol)
    : "";

  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const { data: exchanges = [], isLoading: isLoadingExchanges } =
    useUserExchanges(userId, true);
  const { data: tokenAvailability, isLoading: isLoadingAvailability } =
    useTokenAvailability(userId, normalizedSymbol, open);
  const { data: tokenData } = useTokenData(
    userId,
    normalizedSymbol,
    undefined,
    open && !!normalizedSymbol
  );

  const globallyAvailableExchangeCodes = useMemo(
    () => tokenAvailability?.userExchanges ?? [],
    [tokenAvailability?.userExchanges]
  );
  const globallyAvailableSet = useMemo(
    () => new Set(globallyAvailableExchangeCodes),
    [globallyAvailableExchangeCodes]
  );
  const supportedUserExchangeCodes = useMemo(
    () =>
      exchanges
        .map((e) => e.code)
        .filter((code) => globallyAvailableSet.has(code)),
    [exchanges, globallyAvailableSet]
  );
  const hasTradableExchange = supportedUserExchangeCodes.length > 0;
  const isCheckingAvailability = isLoadingAvailability || isLoadingExchanges;

  const formatPrice = (price: number | null | undefined) => {
    if (!price) return "N/A";
    return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
  };

  const handleTradeClick = () => {
    if (onTrade && normalizedSymbol) {
      onTrade(normalizedSymbol);
      onOpenChange(false);
    }
  };

  // Animation effect
  useEffect(() => {
    if (open && symbol) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      document.body.style.overflow = "hidden";
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      document.body.style.overflow = "";
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, symbol]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onOpenChange(false);
    };
    if (open) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onOpenChange]);

  if (!symbol || !isVisible) return null;

  return (
    <>
      {/* Overlay with fade animation */}
      <div
        className="fixed inset-0 bg-black/60 z-[100] transition-opacity duration-300"
        style={{ opacity: isAnimating ? 1 : 0 }}
        onClick={() => onOpenChange(false)}
      />

      {/* Sheet with slide animation */}
      <div
        className="fixed top-0 right-0 h-full bg-secondary border-l border-border flex flex-col shadow-2xl z-[101] transition-transform duration-300 ease-out"
        style={{
          width: `${SHEET_WIDTH}px`,
          transform: isAnimating ? "translateX(0)" : "translateX(100%)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-xs text-text-secondary">Token Analysis</p>
              <h2 className="text-lg font-semibold text-text-primary">
                {baseCurrency}
              </h2>
            </div>
            <span className="px-2 py-0.5 rounded-full text-xs bg-black/50 text-text-secondary">
              {normalizedSymbol}
            </span>
          </div>
          <div className="flex items-center gap-4">
            {tokenData?.lastPrice && (
              <div className="text-right">
                <p className="text-xs text-text-secondary">Live Price</p>
                <p className="text-sm font-semibold text-text-primary">
                  {formatPrice(tokenData.lastPrice)}
                </p>
              </div>
            )}
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 rounded-lg hover:bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Market Data */}
          {tokenData && (
            <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-4">
              <p className="text-xs uppercase tracking-wide text-text-secondary">
                Market Data
              </p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-text-secondary mb-1">Last Price</p>
                  <p className="text-sm font-semibold text-text-primary">
                    {formatPrice(tokenData.lastPrice)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-1">24h High</p>
                  <p className="text-sm font-semibold text-green-400">
                    {formatPrice(tokenData.highPrice)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-1">24h Low</p>
                  <p className="text-sm font-semibold text-red-400">
                    {formatPrice(tokenData.lowPrice)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Price Chart */}
          <div>
            <h3 className="text-sm font-bold text-text-primary mb-3">
              Live Price Chart
            </h3>
            <TradingViewChartWidget symbol={normalizedSymbol} />
          </div>


          {/* AI Analysis */}
          <TokenAnalysisSection tokenSymbol={baseCurrency} />
        </div>

        {/* Footer - Action Buttons */}
        <div className="sticky bottom-0 bg-secondary border-t border-border p-4 space-y-2">
          {isCheckingAvailability ? (
            <div className="flex items-center justify-center py-3 text-text-secondary text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Checking availability...
            </div>
          ) : hasTradableExchange ? (
            <button
              type="button"
              onClick={handleTradeClick}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white font-semibold bg-accent hover:bg-accent/80 transition-colors"
            >
              Trade {baseCurrency}
            </button>
          ) : (
            <div className="text-center py-3">
              <p className="text-sm text-text-secondary">
                {globallyAvailableExchangeCodes.length > 0
                  ? "Connect an exchange to trade this token"
                  : "This token is not available on supported exchanges"}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
